import styled from "@emotion/styled";
import { gsap } from "gsap";
import { string } from "prop-types";
import React, { useEffect, useReducer, useRef, useState } from "react";

import useMousePosition from "../../../hooks/useMousePosition";
import usePrefersReducedMotion from "../../../hooks/usePrefersReducedMotion";
import theme from "../../../theme";

const gifSize = {
  width: 380,
  height: 250,
};

const Wrapper = styled.div`
  position: fixed;
  top: -${gifSize.height / 2}px;
  left: -${gifSize.width / 2}px;
  flex: 0 1 100%;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  pointer-events: none;
  z-index: 5;
  backface-visibility: hidden;
  will-change: transform;
`;

const InnerWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const ImageContainer = styled.div`
  position: relative;
  overflow: hidden;
  background-color: ${theme.color("primary.base")};
  margin-right: ${theme.space("s")};
  transform-origin: 0% 100%;
`;

const Image = styled.div`
  width: ${gifSize.width}px;
  height: ${gifSize.height}px;
  margin: 0 auto;
  background-image: url(${({ url }) => url});
  background-size: cover;
  background-position: center;
`;

const PrevImage = styled(Image)`
  top: 0;
  left: 0;
  position: absolute;
`;

function AwardsItem({ image, ...props }) {
  const position = useMousePosition();
  const imgWrapperElement = useRef();
  const [initialAnimation, setInitialAnimation] = useState(true);
  const imgElement = useRef([]);

  const prefersReducedMotion = usePrefersReducedMotion();

  const [imageData, setImageData] = useReducer(
    (state) => {
      return prefersReducedMotion
        ? { previous: null, current: image }
        : {
            previous: state.previous === null ? image : state.current,
            current: image,
          };
    },
    {
      current: null,
      previous: null,
    }
  );

  useEffect(() => {
    setInitialAnimation(imageData.previous === null ? true : false);
  }, [imageData.previous]);

  useEffect(() => {
    if (!prefersReducedMotion) {
      if (imgElement.current) {
        const imgTimeline = gsap.timeline();
        imgTimeline
          .add(setImageData())
          .fromTo(
            imgElement.current[0],
            {
              x: initialAnimation ? 0 : -gifSize.width,
              opacity: 0,
            },
            {
              x: 0,
              opacity: 0.75,
            },
            initialAnimation ? 0.2 : 0
          )
          .fromTo(
            imgElement.current[1],
            {
              x: 0,
              opacity: initialAnimation ? 0 : 1,
            },
            {
              x: gifSize.width,
              opacity: 0,
            },
            0
          );
      }
    } else {
      setImageData();
    }
    // eslint-disable-next-line
  }, [image, imgElement, prefersReducedMotion]);

  useEffect(() => {
    const imgWrapperTimeline = gsap.timeline();
    imgWrapperTimeline.fromTo(
      imgWrapperElement.current,
      {
        scale: 0,
        opacity: 0,
      },
      {
        scale: 1,
        opacity: 1,
        duration: 0.5,
        ease: "power3.out",
      },
      0
    );
  }, []);

  return (
    <Wrapper
      {...props}
      style={{
        transform: `translate(${position.x}px, ${position.y}px)`,
      }}
    >
      <InnerWrapper>
        <ImageContainer ref={imgWrapperElement}>
          <Image
            ref={(el) => (imgElement.current[0] = el)}
            url={imageData.current}
          />
          <PrevImage
            ref={(el) => (imgElement.current[1] = el)}
            url={imageData.previous}
          />
        </ImageContainer>
      </InnerWrapper>
    </Wrapper>
  );
}

export const awardsItem = {
  title: string,
  image: string,
};

AwardsItem.propTypes = {
  ...awardsItem,
};

AwardsItem.defaultProps = {};

export default AwardsItem;
